import * as React from 'react';
import { Box, Dialog, DialogContent, DialogTitle, Divider, Grid, IconButton, Stack, Typography, } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DescriptionIcon from '@mui/icons-material/Description';
import CustomButton from '../../../components/buttonComponent/CustomButton';
import { WIZARD_COLORS } from '../../../components/FormComponents/dynamicForm';
import { FormFieldType, } from '../../../interfaces/createApplication';
import { formatFileSize, formatUploadTimestamp } from '../document_upload_service';
import { PREVIEW_DIALOG_PAPER_SX, PREVIEW_LABEL_SX, PREVIEW_RECORD_SX, PREVIEW_SECTION_TITLE_SX, PREVIEW_STEP_TITLE_SX, PREVIEW_VALUE_SX, } from '../CreateApplicationStyles';
var NOT_PROVIDED = '—';
var VALUELESS_FIELD_TYPES = [FormFieldType.Action];
var toDisplayValue = function (field, values) {
    var _a;
    var value = values[field.name];
    if (field.type === FormFieldType.CheckBox) {
        return value ? 'Yes' : 'No';
    }
    if (field.type === FormFieldType.File) {
        return String(value !== null && value !== void 0 ? value : '') || NOT_PROVIDED;
    }
    var rawValue = String(value !== null && value !== void 0 ? value : '').trim();
    if (!rawValue) {
        return NOT_PROVIDED;
    }
    var isChoice = field.type === FormFieldType.Select ||
        field.type === FormFieldType.Radio ||
        field.type === FormFieldType.ChipSelect ||
        field.type === FormFieldType.AutoComplete;
    if (!isChoice) {
        return rawValue;
    }
    var options = field.getOptions
        ? field.getOptions(values)
        : (_a = field.options) !== null && _a !== void 0 ? _a : [];
    var match = options.find(function (option) { return String(option.value) === rawValue; });
    return match ? String(match.label) : rawValue;
};
var visibleSections = function (sections, values) {
    return sections
        .filter(function (section) { return !section.isVisible || section.isVisible(values); })
        .map(function (section) { return ({
        section: section,
        fields: section.groups
            .filter(function (group) { return !group.isVisible || group.isVisible(values); })
            .flatMap(function (group) { return group.fields; })
            .filter(function (field) {
            return !VALUELESS_FIELD_TYPES.includes(field.type) &&
                (!field.isVisible || field.isVisible(values));
        }),
    }); })
        .filter(function (entry) { return entry.fields.length > 0; });
};
var PreviewDocuments = function (_a) {
    var documents = _a.documents;
    return (React.createElement(Box, { sx: PREVIEW_RECORD_SX },
        React.createElement(Typography, { component: "h4", sx: PREVIEW_SECTION_TITLE_SX }, "Attached Documents"),
        React.createElement(Grid, { container: true, spacing: 1.5 }, documents.map(function (uploadedDocument) {
            var uploadedAt = formatUploadTimestamp(uploadedDocument.uploadedAt);
            var meta = [
                formatFileSize(uploadedDocument.fileSize),
                uploadedAt ? "Uploaded ".concat(uploadedAt) : '',
            ]
                .filter(Boolean)
                .join(' • ');
            return (React.createElement(Grid, { item: true, xs: 12, sm: 6, key: uploadedDocument.id },
                React.createElement(Stack, { direction: "row", alignItems: "center", gap: 1 },
                    React.createElement(DescriptionIcon, { "aria-hidden": true, sx: { fontSize: 24, color: WIZARD_COLORS.body } }),
                    React.createElement(Box, { sx: { minWidth: 0 } },
                        React.createElement(Typography, { noWrap: true, sx: PREVIEW_VALUE_SX }, uploadedDocument.fileName),
                        React.createElement(Typography, { sx: PREVIEW_LABEL_SX }, [uploadedDocument.docTypeLabel, meta]
                            .filter(Boolean)
                            .join(' • '))))));
        }))));
};
var PreviewRecord = function (_a) {
    var record = _a.record;
    var sections = visibleSections(record.sections, record.values);
    if (sections.length === 0) {
        return null;
    }
    return (React.createElement(Box, { sx: PREVIEW_RECORD_SX },
        record.label && (React.createElement(Typography, { component: "h4", sx: PREVIEW_SECTION_TITLE_SX }, record.label)),
        sections.map(function (_a) {
            var section = _a.section, fields = _a.fields;
            return (React.createElement(Box, { key: section.id, sx: { mb: 1.5 } },
                section.title && (React.createElement(Typography, { sx: PREVIEW_SECTION_TITLE_SX }, section.title)),
                React.createElement(Grid, { container: true, spacing: 1.5 }, fields.map(function (field) { return (React.createElement(Grid, { item: true, xs: 12, sm: 6, md: 4, key: field.name },
                    React.createElement(Typography, { sx: PREVIEW_LABEL_SX }, field.label),
                    React.createElement(Typography, { sx: PREVIEW_VALUE_SX }, toDisplayValue(field, record.values)))); }))));
        })));
};
var ApplicationPreviewDialog = function (_a) {
    var open = _a.open, title = _a.title, steps = _a.steps, isSubmitting = _a.isSubmitting, onClose = _a.onClose, onSubmit = _a.onSubmit;
    return (React.createElement(Dialog, { open: open, onClose: onClose, fullWidth: true, maxWidth: "md", "aria-labelledby": "application-preview-title", PaperProps: { sx: PREVIEW_DIALOG_PAPER_SX } },
        React.createElement(DialogTitle, { id: "application-preview-title", sx: { display: 'flex', alignItems: 'center', justifyContent: 'space-between' } },
            React.createElement(Box, null,
                React.createElement(Typography, { sx: PREVIEW_STEP_TITLE_SX }, title),
                React.createElement(Typography, { sx: { fontSize: 12, color: WIZARD_COLORS.muted } }, "Please review the details below before submitting.")),
            React.createElement(IconButton, { "aria-label": "Close preview", onClick: onClose, size: "small" },
                React.createElement(CloseIcon, { fontSize: "small" }))),
        React.createElement(DialogContent, { dividers: true }, steps.map(function (step, stepIndex) {
            var _a, _b;
            return (React.createElement(Box, { key: step.label, sx: { mb: 2 } },
                React.createElement(Typography, { component: "h3", sx: PREVIEW_STEP_TITLE_SX }, step.label),
                step.records.map(function (record, recordIndex) {
                    var _a;
                    return (React.createElement(PreviewRecord, { key: (_a = record.label) !== null && _a !== void 0 ? _a : recordIndex, record: record }));
                }),
                Boolean((_a = step.documents) === null || _a === void 0 ? void 0 : _a.length) && (React.createElement(PreviewDocuments, { documents: (_b = step.documents) !== null && _b !== void 0 ? _b : [] })),
                stepIndex < steps.length - 1 && React.createElement(Divider, { sx: { mt: 2 } })));
        })),
        React.createElement(Stack, { direction: "row", justifyContent: "flex-end", gap: 1.5, sx: { p: 2 } },
            React.createElement(CustomButton, { variant: "secondary", MDButton: true, fullWidth: false, onClick: onClose }, "Back To Edit"),
            React.createElement(CustomButton, { variant: "contained", MDButton: true, fullWidth: false, bgColor: WIZARD_COLORS.ink, disabled: isSubmitting, onClick: onSubmit }, isSubmitting ? 'Submitting…' : 'Submit'))));
};
export default React.memo(ApplicationPreviewDialog);
